// Login.js — Role-based Auth (Admin + Candidate)
import { useState } from "react";
import { auth, db, ADMIN_EMAIL } from "./firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { saveInterview } from "./larkService_v2";
import "./Login.css";

function Login({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [mode, setMode] = useState("login"); // login | signup
  const [role, setRole] = useState("candidate"); // candidate | admin

  // Handle Login
  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) {
      setErrorMsg("Please enter both email and password.");
      return;
    }
    setLoading(true);
    setErrorMsg("");

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const user = cred.user;

      // Check role in Firestore
      const userDoc = await getDoc(doc(db, "users", user.uid));
      const userData = userDoc.exists() ? userDoc.data() : null;

      // Admin check
      if (role === "admin") {
        if (user.email !== ADMIN_EMAIL && userData?.role !== "admin") {
          await signOut(auth);
          setErrorMsg("You are not authorized as admin.");
          return;
        }
      }

      // Save/update user doc
      const finalRole = user.email === ADMIN_EMAIL ? "admin" : (userData?.role || "candidate");
      await setDoc(doc(db, "users", user.uid), {
        email: user.email,
        role: finalRole,
        lastLogin: new Date().toISOString(),
      }, { merge: true });

      // Force route using React Router
      navigate(finalRole === "admin" ? "/dashboard" : "/interview", { replace: true });
      onLogin();
    } catch (error) {
      console.error(error);
      switch (error.code) {
        case "auth/user-not-found": setErrorMsg("User not found. Try signing up."); break;
        case "auth/wrong-password": setErrorMsg("Incorrect password."); break;
        case "auth/invalid-email": setErrorMsg("Invalid email format."); break;
        case "auth/invalid-credential": setErrorMsg("Invalid credentials. Check email and password."); break;
        default: setErrorMsg("Login failed. Please try again.");
      }
      setLoading(false);
    }
  };

  // Handle Signup
  const handleSignup = async () => {
    if (!email.trim() || !password.trim()) {
      setErrorMsg("Please enter both email and password.");
      return;
    }
    if (password.length < 6) {
      setErrorMsg("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    setErrorMsg("");

    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const user = cred.user;

      // Save user with role
      const userRole = user.email === ADMIN_EMAIL ? "admin" : "candidate";
      await setDoc(doc(db, "users", user.uid), {
        email: user.email,
        role: userRole,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
      });

      // Save user details to Lark base (interviews table)
      try {
        await saveInterview({
          userId: user.uid,
          candidateName: user.email.split("@")[0], // Fallback name from email
          positionTitle: "Not Selected",
          interviewScore: 0,
          finalScore: 0,
          answers: [],
          status: "pending"
        });
        console.log("Successfully saved user details to Lark base.");
      } catch (err) {
        console.error("Failed to save user to Lark interviews table:", err);
      }

      // Force route using React Router
      navigate(userRole === "admin" ? "/dashboard" : "/interview", { replace: true });
      onLogin();
    } catch (error) {
      console.error(error);
      switch (error.code) {
        case "auth/email-already-in-use": setErrorMsg("Email already registered. Try logging in."); break;
        case "auth/weak-password": setErrorMsg("Password is too weak. Use at least 6 characters."); break;
        case "auth/invalid-email": setErrorMsg("Invalid email format."); break;
        default: setErrorMsg("Signup failed. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (mode === "login") handleLogin();
    else handleSignup();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <div className="login-container">
      {/* Advanced BMW-style Animated Background */}
      <div className="center-glow"></div>
      <div className="light-beam beam-1"></div>
      <div className="light-beam beam-2"></div>
      <div className="light-beam beam-3"></div>

      <div className="login-card">
        <h2>AI Hiring Platform</h2>

        {/* Role Selector */}
        <div className="role-tabs">
          <button
            className={`role-tab ${role === "candidate" ? "active" : ""}`}
            onClick={() => setRole("candidate")}
          >
            👤 Candidate
          </button>
          <button
            className={`role-tab ${role === "admin" ? "active" : ""}`}
            onClick={() => setRole("admin")}
          >
            🔒 Admin
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="mode-toggle">
          <button
            className={`mode-btn ${mode === "login" ? "active" : ""}`}
            onClick={() => { setMode("login"); setErrorMsg(""); }}
          >
            Login
          </button>
          <button
            className={`mode-btn ${mode === "signup" ? "active" : ""}`}
            onClick={() => { setMode("signup"); setErrorMsg(""); }}
          >
            Sign Up
          </button>
        </div>

        {/* Inputs */}
        {errorMsg && <div className="error-msg">{errorMsg}</div>}
        <input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyPress}
          autoFocus
        />
        <input
          type="password"
          placeholder={mode === "signup" ? "Create password (min 6 chars)" : "Password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyPress}
        />

        <button onClick={handleSubmit} disabled={loading}>
          {loading
            ? (mode === "login" ? "Logging in..." : "Creating account...")
            : (mode === "login" ? `Login as ${role === "admin" ? "Admin" : "Candidate"}` : "Create Account")
          }
        </button>

        <div className="login-footer">
          {mode === "login"
            ? <p>Don't have an account? <button className="link-text" onClick={() => setMode("signup")}>Sign up</button></p>
            : <p>Already have an account? <button className="link-text" onClick={() => setMode("login")}>Log in</button></p>
          }
        </div>
      </div>
    </div>
  );
}

export default Login;